/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { DashboardSection } from '../DashboardSection';
import { useContext } from 'react';
import { SubjectContext } from 'blb-contexts/Subject';
import { UnavailableMessage } from './UnavailableMessage';
import { initialTitleConfigurationsValues, titleConfigurationToCopy, TitleFeatureSections } from './constants';
import { getConfig } from 'blb-utils/config';

export interface AgencyConfigurationWrapperProps {
  children: JSX.Element;
  hideDashboardSection?: boolean;
  section: TitleFeatureSections;
}

export const AgencyConfigurationWrapper: React.FC<AgencyConfigurationWrapperProps> = ({
  section,
  hideDashboardSection,
  children,
}) => {
  const { useAgencyConfigurations } = getConfig();
  const context = useContext(SubjectContext);
  const loan = context?.loan!;
  const agency = context?.agency!;

  const isFeatureEnabledForLender = () => {
    const lenderConfigurations = {
      feeCollaboration: loan.featureFlags?.isFeeCollabEnabled,
      sharedInbox: loan.featureFlags?.isSharedInboxEnabled,
      titleCurative: loan.featureFlags?.isTitleCurativeEnabled,
    };

    return lenderConfigurations[section];
  };

  const isFeatureEnabledForAgency = () => {
    const titleConfigurations = agency.generalTitleConfiguration || initialTitleConfigurationsValues;
    return titleConfigurations[section];
  };

  const getSectionUnavailableMessage = () => {
    const featureName = titleConfigurationToCopy[section].featureName;

    // Agency message takes precedence since Title Agents are aware when features are disabled on their end
    // Displaying the lender message if disabled on both ends would cause confusion as Title Agents might
    // assume the feature is enabled on their end
    if (!isFeatureEnabledForAgency()) {
      return `${featureName} with ${agency.name} isn’t available because the feature isn’t turned on at this agency.`;
    }

    if (!isFeatureEnabledForLender()) {
      return `${featureName} is disabled for use with ${loan.lender}.`;
    }

    return null;
  };

  const renderContent = () => {
    const unavailableMessage = getSectionUnavailableMessage();
    if (useAgencyConfigurations && unavailableMessage) {
      return hideDashboardSection ? (
        <UnavailableMessage message={unavailableMessage} />
      ) : (
        <DashboardSection
          sectionConfigs={[
            {
              content: <UnavailableMessage message={unavailableMessage} />,
              hideTitle: true,
              title: section,
            },
          ]}
          sectionHeader={titleConfigurationToCopy[section].sectionName}
        />
      );
    }

    return children;
  };

  return renderContent();
};
